// Shared client-side validators for numeric quantities + entry dates.
// Backend enforces the same rules (see server.py validate_entry_date /
// validate_positive_quantity); these helpers exist so the UI can surface
// clear, specific messages *before* a round-trip, and so we can show the
// "log historical entry?" confirm dialog for dates > 5 years old.

const FIVE_YEARS_MS = 5 * 365 * 24 * 3600 * 1000;

/**
 * @param {string} dateStr YYYY-MM-DD (or ISO prefix)
 * @param {{ field?: string }} opts
 * @returns {{ ok: true } | { ok: false, reason: 'invalid'|'future'|'too_old', message: string }}
 */
export function validateDate(dateStr, { field = 'Date' } = {}) {
  if (!dateStr) return { ok: false, reason: 'invalid', message: `${field} is required` };
  const d = new Date(dateStr.length > 10 ? dateStr : `${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return { ok: false, reason: 'invalid', message: `${field} is not valid` };
  // Compare in local calendar-day terms — a date typed today should never be
  // considered "future" just because of timezone rounding.
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const entry = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (entry.getTime() > today.getTime()) {
    return { ok: false, reason: 'future', message: `${field} cannot be in the future` };
  }
  if (today.getTime() - entry.getTime() > FIVE_YEARS_MS) {
    return { ok: false, reason: 'too_old', message: `${field} is more than 5 years old — log as a historical entry?` };
  }
  return { ok: true };
}

/**
 * @param {number|string} value
 * @param {{ field?: string, max?: number, min?: number, allowZero?: boolean }} opts
 */
export function validateQuantity(value, { field = 'Value', max, min = 0, allowZero = false } = {}) {
  if (value === '' || value === null || value === undefined) {
    return { ok: false, message: `${field} is required` };
  }
  const v = Number(value);
  if (Number.isNaN(v)) return { ok: false, message: `${field} must be a number` };
  if (!allowZero && v <= 0) return { ok: false, message: `${field} must be greater than 0` };
  if (allowZero && v < 0) return { ok: false, message: `${field} cannot be negative` };
  if (min !== undefined && v < min) return { ok: false, message: `${field} must be at least ${min}` };
  if (max !== undefined && v > max) return { ok: false, message: `${field} looks too high (max ${max}). Please double-check.` };
  return { ok: true, value: v };
}

// Weight ceilings by unit — mirrors backend weight_max_for_unit(). Used for
// weight logs on Foundation animals + herd-level weight logs.
export function maxWeightForUnit(unit = 'lbs') {
  const u = String(unit).toLowerCase();
  if (u === 'kg') return 1400;
  if (u === 'oz') return 3000 * 16;
  if (u === 'g') return 1400 * 1000;
  return 3000;
}
